import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
    CognitoUserPool,
    CognitoUser,
    AuthenticationDetails,
    CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

const POOL_DATA = {
    UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
    ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
};

const userPool = POOL_DATA.UserPoolId ? new CognitoUserPool(POOL_DATA) : null;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const auth = useAuthInternal();
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export default function useAuth() {
    return useContext(AuthContext);
}

function useAuthInternal() {
    const [user, setUser] = useState(null);        // { email, userId }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionRestored, setSessionRestored] = useState(false);

    // Check for existing session on mount
    useEffect(() => {
        if (!userPool) {
            setLoading(false);
            return;
        }
        const cognitoUser = userPool.getCurrentUser();
        if (!cognitoUser) {
            setLoading(false);
            return;
        }
        cognitoUser.getSession((err, session) => {
            if (err || !session?.isValid()) {
                setLoading(false);
                return;
            }
            const payload = session.getIdToken().decodePayload();
            setUser({ email: payload.email, userId: payload.sub });
            // Only restore session history on an explicit page reload (F5 / Cmd+R).
            // Fresh tab, first-time load, and SPA signIn flows all have type 'navigate',
            // which means they should start with an empty chat even if tokens exist.
            const navType = performance?.getEntriesByType?.('navigation')?.[0]?.type;
            if (navType === 'reload') setSessionRestored(true);
            setLoading(false);
        });
    }, []);

    const getIdToken = useCallback(() => {
        return new Promise((resolve) => {
            if (!userPool) return resolve(null);
            const cognitoUser = userPool.getCurrentUser();
            if (!cognitoUser) return resolve(null);
            cognitoUser.getSession((err, session) => {
                if (err || !session?.isValid()) return resolve(null);
                resolve(session.getIdToken().getJwtToken());
            });
        });
    }, []);

    const signUp = useCallback((email, password) => {
        return new Promise((resolve, reject) => {
            if (!userPool) return reject(new Error("Auth not configured."));
            setError(null);
            const attrs = [
                new CognitoUserAttribute({ Name: 'email', Value: email }),
            ];
            userPool.signUp(email, password, attrs, null, (err, result) => {
                if (err) {
                    setError(err.message);
                    return reject(err);
                }
                resolve(result);
            });
        });
    }, []);

    const confirmSignUp = useCallback((email, code) => {
        return new Promise((resolve, reject) => {
            if (!userPool) return reject(new Error("Auth not configured."));
            setError(null);
            const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
            cognitoUser.confirmRegistration(code, true, (err, result) => {
                if (err) {
                    setError(err.message);
                    return reject(err);
                }
                resolve(result);
            });
        });
    }, []);

    const signIn = useCallback((email, password) => {
        return new Promise((resolve, reject) => {
            if (!userPool) return reject(new Error("Auth not configured."));
            setError(null);
            const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
            const authDetails = new AuthenticationDetails({ Username: email, Password: password });
            cognitoUser.authenticateUser(authDetails, {
                onSuccess: (session) => {
                    const payload = session.getIdToken().decodePayload();
                    setUser({ email: payload.email, userId: payload.sub });
                    resolve(session);
                },
                onFailure: (err) => {
                    setError(err.message);
                    reject(err);
                },
            });
        });
    }, []);

    const signOut = useCallback(() => {
        if (!userPool) return;
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) cognitoUser.signOut();
        setUser(null);
        setError(null);
        setSessionRestored(false);
    }, []);

    const forgotPassword = useCallback((email) => {
        return new Promise((resolve, reject) => {
            if (!userPool) return reject(new Error("Auth not configured."));
            setError(null);
            const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
            cognitoUser.forgotPassword({
                onSuccess: resolve,
                onFailure: (err) => { setError(err.message); reject(err); },
            });
        });
    }, []);

    const confirmPassword = useCallback((email, code, newPassword) => {
        return new Promise((resolve, reject) => {
            if (!userPool) return reject(new Error("Auth not configured."));
            setError(null);
            const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
            cognitoUser.confirmPassword(code, newPassword, {
                onSuccess: resolve,
                onFailure: (err) => { setError(err.message); reject(err); },
            });
        });
    }, []);

    return {
        user,
        loading,
        error,
        setError,
        sessionRestored,
        getIdToken,
        signUp,
        confirmSignUp,
        signIn,
        signOut,
        forgotPassword,
        confirmPassword,
    };
}
